// Marketplace Edit Form Component
// Feature: 006-marketplace-management

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, X, Upload, Trash2 } from 'lucide-react';
import { LocationPicker } from './LocationPicker';
import { buildImageUrl } from '@/services/api/images';
import type {
  MarketplaceItemWithSeller,
  MarketplaceItemUpdate,
  CategoryOption,
  ItemPhoto,
} from '@/types/marketplaceAdmin';

interface MarketplaceEditFormProps {
  item: MarketplaceItemWithSeller;
  categories: CategoryOption[];
  onSave: (updates: MarketplaceItemUpdate, newPhotos: File[], removedPhotoUrls: string[]) => Promise<{
    success: boolean;
    error?: string;
  }>;
  onCancel: () => void;
}

export function MarketplaceEditForm({
  item,
  categories,
  onSave,
  onCancel,
}: MarketplaceEditFormProps) {
  const { t } = useTranslation('marketplaceAdmin');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [title, setTitle] = useState(item.data.title);
  const [description, setDescription] = useState(item.data.description ?? '');
  const [price, setPrice] = useState(item.data.price.toString());
  const [categoryId, setCategoryId] = useState(item.category_id || '');
  const [tags, setTags] = useState(item.data.tags?.join(', ') || '');
  const [offerEnabled, setOfferEnabled] = useState(item.data.offer_enabled ?? false);
  const [offerMin, setOfferMin] = useState(item.data.offer_min?.toString() || '');
  const [offerMax, setOfferMax] = useState(item.data.offer_max?.toString() || '');
  const [latitude, setLatitude] = useState(item.data.latitude?.toString() || '');
  const [longitude, setLongitude] = useState(item.data.longitude?.toString() || '');
  const [location, setLocation] = useState(item.data.location || '');

  // Photo state
  const [existingPhotos, setExistingPhotos] = useState<ItemPhoto[]>(item.data.photos || []);
  const [removedPhotoUrls, setRemovedPhotoUrls] = useState<string[]>([]);
  const [newPhotoFiles, setNewPhotoFiles] = useState<File[]>([]);
  const [newPhotoPreviews, setNewPhotoPreviews] = useState<{ file: File; preview: string }[]>([]);

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when item data changes
  useEffect(() => {
    setTitle(item.data.title);
    setDescription(item.data.description ?? '');
    setPrice(item.data.price.toString());
    setCategoryId(item.category_id || '');
    setTags(item.data.tags?.join(', ') || '');
    setOfferEnabled(item.data.offer_enabled ?? false);
    setOfferMin(item.data.offer_min?.toString() || '');
    setOfferMax(item.data.offer_max?.toString() || '');
    setLatitude(item.data.latitude?.toString() || '');
    setLongitude(item.data.longitude?.toString() || '');
    setLocation(item.data.location || '');
    setExistingPhotos(item.data.photos || []);
    setRemovedPhotoUrls([]);
    setNewPhotoFiles([]);
    setNewPhotoPreviews([]);
  }, [item]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      newPhotoPreviews.forEach(({ preview }) => URL.revokeObjectURL(preview));
    };
  }, [newPhotoPreviews]);

  // Ref for keyboard shortcut handler to avoid stale closures
  const handleSubmitRef = useRef<(() => Promise<void>) | null>(null);

  // Validate form
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Title validation
    if (!title.trim()) {
      newErrors.title = t('edit.validation.titleRequired');
    } else if (title.trim().length < 3) {
      newErrors.title = t('edit.validation.titleMinLength');
    } else if (title.trim().length > 100) {
      newErrors.title = t('edit.validation.titleMaxLength');
    }

    // Description validation
    if (!description.trim()) {
      newErrors.description = t('edit.validation.descriptionRequired');
    } else if (description.trim().length < 10) {
      newErrors.description = t('edit.validation.descriptionMinLength');
    } else if (description.trim().length > 2000) {
      newErrors.description = t('edit.validation.descriptionMaxLength');
    }

    // Price validation
    const priceNum = parseFloat(price);
    if (!price || isNaN(priceNum)) {
      newErrors.price = t('edit.validation.priceRequired');
    } else if (priceNum <= 0) {
      newErrors.price = t('edit.validation.pricePositive');
    }

    // Category validation
    if (!categoryId) {
      newErrors.category = t('edit.validation.categoryRequired');
    }

    // Offer validation
    if (offerEnabled) {
      const priceValue = parseFloat(price) || 0;
      const minValue = parseFloat(offerMin) || 0;
      const maxValue = parseFloat(offerMax) || priceValue;

      if (minValue >= priceValue) {
        newErrors.offerMin = t('edit.validation.offerMinInvalid');
      }
      if (maxValue > priceValue || maxValue < minValue) {
        newErrors.offerMax = t('edit.validation.offerMaxInvalid');
      }
    }

    // Location validation
    if (latitude) {
      const lat = parseFloat(latitude);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        newErrors.latitude = t('edit.validation.latitudeInvalid');
      }
    }

    if (longitude) {
      const lng = parseFloat(longitude);
      if (isNaN(lng) || lng < -180 || lng > 180) {
        newErrors.longitude = t('edit.validation.longitudeInvalid');
      }
    }

    if (location.length > 200) {
      newErrors.location = t('edit.validation.locationMaxLength');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle photo file selection
  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const newErrors: Record<string, string> = {};
    const validFiles: { file: File; preview: string }[] = [];

    for (const file of files) {
      // Validate size
      if (file.size > 5 * 1024 * 1024) {
        newErrors.photos = t('edit.validation.photoSize');
        continue;
      }

      // Validate type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        newErrors.photos = t('edit.validation.photoType');
        continue;
      }

      // Create preview URL
      const preview = URL.createObjectURL(file);
      validFiles.push({ file, preview });
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors({ ...errors, ...newErrors });
    }

    setNewPhotoFiles((prev) => [...prev, ...validFiles.map((v) => v.file)]);
    setNewPhotoPreviews((prev) => [...prev, ...validFiles]);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove a new photo preview
  const removeNewPhoto = (index: number) => {
    const preview = newPhotoPreviews[index];
    URL.revokeObjectURL(preview.preview);
    setNewPhotoFiles((prev) => prev.filter((_, i) => i !== index));
    setNewPhotoPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  // Remove an existing photo
  const removeExistingPhoto = (url: string) => {
    setExistingPhotos((prev) => prev.filter((p) => p.url !== url));
    setRemovedPhotoUrls((prev) => [...prev, url]);
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (isSaving) return; // Prevent double submit
    if (!validate()) return;

    setIsSaving(true);
    setErrors({});

    // Parse tags from comma-separated string
    const parsedTags = tags
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    const updates: MarketplaceItemUpdate = {
      title: title.trim(),
      description: description.trim(),
      price: parseFloat(price),
      category_id: categoryId,
      tags: parsedTags,
      offer_enabled: offerEnabled,
      offer_min: offerEnabled && offerMin ? parseFloat(offerMin) : undefined,
      offer_max: offerEnabled && offerMax ? parseFloat(offerMax) : undefined,
      latitude: latitude ? parseFloat(latitude) : null,
      longitude: longitude ? parseFloat(longitude) : null,
      location: location.trim() || null,
    };

    try {
      const result = await onSave(updates, newPhotoFiles, removedPhotoUrls);

      if (!result.success && result.error) {
        setErrors({ general: result.error });
      }
    } catch (err) {
      setErrors({ general: t('edit.validation.saveFailed') });
    } finally {
      setIsSaving(false);
    }
  };

  // Update ref after handleSubmit is defined
  handleSubmitRef.current = handleSubmit;

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        handleSubmitRef.current?.();
      } else if (e.key === 'Escape') {
        onCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-neutral-200">
        <h2 className="text-2xl font-bold text-neutral-900">{t('edit.title')}</h2>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50 transition disabled:opacity-50"
          >
            <X size={18} />
            {t('edit.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50"
          >
            {isSaving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Save size={18} />
            )}
            {t('edit.save')}
          </button>
        </div>
      </div>

      {/* General Error */}
      {errors.general && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {errors.general}
        </div>
      )}

      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-neutral-900">{t('edit.sections.basic')}</h3>

        {/* Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-neutral-700 mb-1">
            {t('edit.fields.title')} <span className="text-red-500">*</span>
          </label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.title ? 'border-red-300' : 'border-neutral-300'
            }`}
            disabled={isSaving}
          />
          {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
        </div>

        {/* Category */}
        <div>
          <label htmlFor="category" className="block text-sm font-medium text-neutral-700 mb-1">
            {t('edit.fields.category')} <span className="text-red-500">*</span>
          </label>
          <select
            id="category"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.category ? 'border-red-300' : 'border-neutral-300'
            }`}
            disabled={isSaving}
          >
            <option value="">{t('edit.fields.selectCategory')}</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
          {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category}</p>}
        </div>
      </div>

      {/* Description */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-neutral-900">{t('edit.sections.description')}</h3>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-neutral-700 mb-1">
            {t('edit.fields.description')} <span className="text-red-500">*</span>
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.description ? 'border-red-300' : 'border-neutral-300'
            }`}
            disabled={isSaving}
          />
          {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
        </div>
      </div>

      {/* Pricing */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-neutral-900">{t('edit.sections.pricing')}</h3>

        <div>
          <label htmlFor="price" className="block text-sm font-medium text-neutral-700 mb-1">
            {t('edit.fields.price')} ({t('edit.fields.priceCurrency')}) <span className="text-red-500">*</span>
          </label>
          <input
            id="price"
            type="number"
            min="0"
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.price ? 'border-red-300' : 'border-neutral-300'
            }`}
            disabled={isSaving}
          />
          {errors.price && <p className="mt-1 text-sm text-red-600">{errors.price}</p>}
        </div>
      </div>

      {/* Offers */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-neutral-900">{t('edit.sections.offers')}</h3>

        <div className="flex items-center gap-2">
          <input
            id="offerEnabled"
            type="checkbox"
            checked={offerEnabled}
            onChange={(e) => setOfferEnabled(e.target.checked)}
            className="w-4 h-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
            disabled={isSaving}
          />
          <label htmlFor="offerEnabled" className="text-sm font-medium text-neutral-700">
            {t('edit.fields.offerEnabled')}
          </label>
        </div>

        {offerEnabled && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="offerMin" className="block text-sm font-medium text-neutral-700 mb-1">
                {t('edit.fields.offerMin')}
              </label>
              <input
                id="offerMin"
                type="number"
                min="0"
                step="0.01"
                value={offerMin}
                onChange={(e) => setOfferMin(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.offerMin ? 'border-red-300' : 'border-neutral-300'
                }`}
                disabled={isSaving}
              />
              {errors.offerMin && <p className="mt-1 text-sm text-red-600">{errors.offerMin}</p>}
            </div>
            <div>
              <label htmlFor="offerMax" className="block text-sm font-medium text-neutral-700 mb-1">
                {t('edit.fields.offerMax')}
              </label>
              <input
                id="offerMax"
                type="number"
                min="0"
                step="0.01"
                value={offerMax}
                onChange={(e) => setOfferMax(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.offerMax ? 'border-red-300' : 'border-neutral-300'
                }`}
                disabled={isSaving}
              />
              {errors.offerMax && <p className="mt-1 text-sm text-red-600">{errors.offerMax}</p>}
            </div>
          </div>
        )}
      </div>

      {/* Tags */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-neutral-900">{t('edit.sections.tags')}</h3>

        <div>
          <label htmlFor="tags" className="block text-sm font-medium text-neutral-700 mb-1">
            {t('edit.fields.tags')}
          </label>
          <input
            id="tags"
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder={t('edit.fields.tagsPlaceholder')}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            disabled={isSaving}
          />
        </div>
      </div>

      {/* Location */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-neutral-900">{t('edit.sections.location')}</h3>

        <div>
          <label htmlFor="location" className="block text-sm font-medium text-neutral-700 mb-1">
            {t('edit.fields.location')}
          </label>
          <input
            id="location"
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder={t('edit.fields.locationPlaceholder')}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.location ? 'border-red-300' : 'border-neutral-300'
            }`}
            disabled={isSaving}
          />
          {errors.location && <p className="mt-1 text-sm text-red-600">{errors.location}</p>}
        </div>

        {/* Location Map */}
        <LocationPicker
          latitude={latitude ? parseFloat(latitude) : null}
          longitude={longitude ? parseFloat(longitude) : null}
          onChange={(lat, lng, address) => {
            setLatitude(lat.toString());
            setLongitude(lng.toString());
            // Auto-fill location field if address was geocoded
            if (address) {
              setLocation(address);
            }
          }}
          disabled={isSaving}
        />
        {errors.latitude && <p className="mt-1 text-sm text-red-600">{errors.latitude}</p>}
        {errors.longitude && <p className="mt-1 text-sm text-red-600">{errors.longitude}</p>}
      </div>

      {/* Photos */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-neutral-900">{t('edit.sections.photos')}</h3>

        {/* Existing Photos Grid */}
        {existingPhotos.length > 0 && (
          <div>
            <p className="text-sm text-neutral-600 mb-2">{t('edit.fields.currentPhotos')}</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {existingPhotos.map((photo, idx) => (
                <div key={idx} className="relative group">
                  <img
                    src={buildImageUrl(photo.thumbnail_url || photo.url, 'card')}
                    alt={`Photo ${idx + 1}`}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  {idx === 0 && (
                    <span className="absolute top-2 left-2 px-2 py-1 bg-primary-600 text-white text-xs rounded">
                      Primary
                    </span>
                  )}
                  <button
                    onClick={() => removeExistingPhoto(photo.url)}
                    disabled={isSaving}
                    className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition opacity-0 group-hover:opacity-100 disabled:opacity-50"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* New Photo Previews */}
        {newPhotoPreviews.length > 0 && (
          <div>
            <p className="text-sm text-neutral-600 mb-2">{t('edit.fields.newPhotos')}</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {newPhotoPreviews.map((item, idx) => (
                <div key={idx} className="relative group">
                  <img
                    src={item.preview}
                    alt={`New photo ${idx + 1}`}
                    className="w-full h-32 object-cover rounded-lg border-2 border-primary-500"
                  />
                  <button
                    onClick={() => removeNewPhoto(idx)}
                    disabled={isSaving}
                    className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition disabled:opacity-50"
                  >
                    <Trash2 size={14} />
                  </button>
                  <span className="absolute top-2 left-2 px-2 py-1 bg-green-600 text-white text-xs rounded">
                    New
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload Button */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={handlePhotoSelect}
            className="hidden"
            disabled={isSaving}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50 transition disabled:opacity-50"
          >
            <Upload size={18} />
            {t('edit.fields.addPhotos')}
          </button>
          <p className="mt-1 text-sm text-neutral-500">{t('edit.fields.photoHint')}</p>
          {errors.photos && <p className="mt-1 text-sm text-red-600">{errors.photos}</p>}
        </div>
      </div>

      {/* Save hint */}
      <div className="pt-4 border-t border-neutral-200">
        <p className="text-sm text-neutral-500">{t('edit.saveHint')}</p>
      </div>
    </div>
  );
}
